/**
 * @fileoverview Production Order table component
 * Displays production orders synchronized from SAP
 */
import React, { forwardRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { intl } from "dmsbasereact";
import { Chip } from "@mui/material";
import { DmsEntityTable } from "../DmsEntityTable";

/**
 * Format datetime for display
 * @param {string} dateStr - Date string
 * @returns {string} Formatted datetime
 */
const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Production Order table component using DmsEntityTable
 */
const ProductionOrderTable = forwardRef(({ onViewDetails = null }, ref) => {
  // Column definitions
  const columns = useMemo(() => [
    {
      label: intl.get("OrderNumber").d("Order Number"),
      column_name: "order_number",
      filter: true,
      width: "150px",
    },
    {
      label: intl.get("ScheduledTo").d("Scheduled For"),
      column_name: "scheduled_to",
      width: "180px",
      function: formatDateTime,
    },
    {
      label: intl.get("Status").d("Status"),
      column_name: "status",
      width: "120px",
      element: (row) => {
        // Determine status based on your business logic
        // This is a placeholder - adjust according to your ProductionOrder model
        const now = new Date();
        const scheduled = new Date(row.scheduled_to);

        let color = "warning";
        let label = intl.get("Pending").d("Pending");

        if (scheduled < now) {
          color = "success";
          label = intl.get("Completed").d("Completed");
        } else if (Math.abs(scheduled - now) < 24 * 60 * 60 * 1000) {
          color = "info";
          label = intl.get("InProgress").d("In Progress");
        }

        return (
          <Chip
            label={label}
            size="small"
            color={color}
          />
        );
      },
    },
    {
      label: intl.get("CreatedOn").d("Created On"),
      column_name: "created_on",
      width: "180px",
      function: formatDateTime,
    },
  ], []);

  // View details handler
  const handleViewDetails = useCallback((row) => {
    if (onViewDetails) {
      onViewDetails(row);
    }
  }, [onViewDetails]);

  return (
    <DmsEntityTable
      ref={ref}
      url="/production_order/table"
      columns={columns}
      onView={handleViewDetails}
      hideDelete={true}
      hideBlock={true}
      hideCreate={true}
      hidePdf={true}
      hideExport={true}
      createButtonLabel={intl.get("ProductionOrdersReadOnly").d("Dados sincronizados do SAP (somente leitura)")}
    />
  );
});

ProductionOrderTable.displayName = "ProductionOrderTable";

ProductionOrderTable.propTypes = {
  onViewDetails: PropTypes.func,
};


export { ProductionOrderTable };


